import { lazy, Suspense, useState, useMemo, useEffect, useRef, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { HeroSection } from "@/components/HeroSection";
import { Footer } from "@/components/Footer";
import { ShelterMap } from "@/components/ShelterMap";
import { ShelterList } from "@/components/ShelterList";
import { ShelterListSkeleton } from "@/components/ShelterListSkeleton";
import { ShelterFiltersComponent } from "@/components/ShelterFilters";
const ShelterDetail = lazy(() =>
  import("@/components/ShelterDetail").then((m) => ({ default: m.ShelterDetail }))
);
import { LocationBlockedInfo } from "@/components/LocationBlockedInfo";
import { CommunityPopup } from "@/components/CommunityPopup";
import { ErrorScreen } from "@/components/ErrorScreen";
import { OfflineBanner } from "@/components/OfflineBanner";
import { useShelters } from "@/hooks/useShelters";
import { useShelterSections } from "@/hooks/useShelterSections";
import { useSheltersWithFundraiser } from "@/hooks/useSheltersWithFundraiser";
import { useSheltersWithNamedAdoptions } from "@/hooks/useSheltersWithNamedAdoptions";
import { useSheltersWithUrgentSection } from "@/hooks/useSheltersWithUrgentSection";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { useCookieConsent } from "@/hooks/useCookieConsent";
import { useSectionTracking } from "@/hooks/useSectionTracking";
import type { Shelter, ShelterFilters } from "@/types/shelter";
import { isUrgentNeedsActive } from "@/types/shelter";
import { X, Locate, Cookie } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";
import { includesNormalized, matchesWordPrefix, matchesShelterQuery } from "@/lib/geoText";
import type { EventSource as AnalyticsEventSource } from "@/lib/analyticsEvents";
import { ScrollProgressBar } from "@/components/ScrollProgressBar";
import { trackEvent } from "@/hooks/useEventTracking";
import { prefetchQuickUpdate, prefetchShelterPage, prefetchPanel } from "@/lib/routePrefetch";
import { LOADING_SLOW } from "@/lib/uiMessages";

// Calculate distance in km between two points
const calculateDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

export interface ShelterWithDistance extends Shelter {
  distance?: number;
}

const Index = () => {
  const { data: shelters, isLoading, error, refetch, isFetching, failureCount } = useShelters();
  const { data: shelterIdsWithFundraiser } = useSheltersWithFundraiser();
  const { data: shelterIdsWithNamedAdoptions } = useSheltersWithNamedAdoptions();
  const { data: shelterIdsWithUrgentSection } = useSheltersWithUrgentSection();
  const isOnline = useOnlineStatus();
  const { hasConsent } = useCookieConsent();
  const [selectedShelter, setSelectedShelter] = useState<Shelter | null>(null);
  const [selectedShelterSource, setSelectedShelterSource] = useState<AnalyticsEventSource>("unknown");
  const [showDetail, setShowDetail] = useState(false);
  const [isMapFullscreen, setIsMapFullscreen] = useState(false);
  const [userLocation, setUserLocation] = useState<[number, number] | null>(null);
  const [isLocating, setIsLocating] = useState(false);
  const [locationDismissed, setLocationDismissed] = useState(false);
  const [locationBlocked, setLocationBlocked] = useState<{ show: boolean; platform: "ios" | "android" | "desktop" }>({ show: false, platform: "desktop" });
  const [scrollPosition, setScrollPosition] = useState({ atTop: true, atBottom: false });
  const [mapBounds, setMapBounds] = useState<{ north: number; south: number; east: number; west: number } | null>(null);
  const [filterByMapView, setFilterByMapView] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<HTMLDivElement>(null);

  // Section tracking
  const heroRef = useSectionTracking("Hero");
  const filtersRef = useSectionTracking("Filtry");
  const mapSectionRef = useSectionTracking("Mapa", 0.2);
  const listRef = useSectionTracking("Lista schronisk", 0.2);
  const footerRef = useSectionTracking("Stopka");

  const handleScrollToMap = useCallback(() => {
    mapRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);
  const [filters, setFilters] = useState<ShelterFilters>({
    searchQuery: "",
    dogWalking: false,
    volunteering: false,
    urgentNeeds: false,
    hasFundraiser: false,
    hasNamedAdoptions: false,
    dogsAvailable: false,
    catsAvailable: false,
    organizationVerified: false,
    city: "",
  });

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return filters.searchQuery !== "" ||
      filters.dogWalking ||
      filters.volunteering ||
      filters.urgentNeeds ||
      filters.hasFundraiser ||
      filters.hasNamedAdoptions ||
      filters.dogsAvailable ||
      filters.catsAvailable ||
      filters.organizationVerified ||
      filters.city !== "";
  }, [filters]);

  // Clear all filters
  const handleClearFilters = () => {
    setFilters({
      searchQuery: "",
      dogWalking: false,
      volunteering: false,
      urgentNeeds: false,
      hasFundraiser: false,
      hasNamedAdoptions: false,
      dogsAvailable: false,
      catsAvailable: false,
      organizationVerified: false,
      city: "",
    });
  };

  // Get unique cities for filter dropdown
  const cities = useMemo(() => {
    if (!shelters) return [];
    const uniqueCities = [...new Set(shelters.map((s) => s.city))];
    return uniqueCities.sort();
  }, [shelters]);

  // Helper function to check if shelter has exact location
  const hasExactLocation = (shelter: Shelter): boolean => {
    if (!shelter.address) return false;
    const addressLower = shelter.address.toLowerCase().trim();
    const cityLower = shelter.city.toLowerCase().trim();
    if (addressLower === '' || addressLower === cityLower) return false;
    if (addressLower === `${cityLower}, polska` || addressLower === `${cityLower}, poland`) return false;
    return true;
  };

  // Filter and sort shelters based on active filters and user location
  const filteredShelters = useMemo((): ShelterWithDistance[] => {
    if (!shelters) return [];
    
    let result = shelters.filter((shelter) => {
      // Search query filter - tokenizowane dopasowanie (patrz matchesShelterQuery)
      if (filters.searchQuery) {
        if (!matchesShelterQuery(shelter.name, shelter.city, shelter.region, filters.searchQuery)) {
          return false;
        }
      }
      
      if (filters.dogWalking && shelter.dog_walking_available !== true) return false;
      if (filters.volunteering && shelter.volunteering_available !== true) return false;
      if (filters.urgentNeeds && !isUrgentNeedsActive(shelter) && !shelterIdsWithUrgentSection?.has(shelter.id)) return false;
      if (filters.hasFundraiser && !shelterIdsWithFundraiser?.has(shelter.id)) return false;
      if (filters.hasNamedAdoptions && !shelterIdsWithNamedAdoptions?.has(shelter.id)) return false;
      if (filters.dogsAvailable && shelter.dogs_available !== true) return false;
      if (filters.catsAvailable && shelter.cats_available !== true) return false;
      if (filters.organizationVerified && shelter.organization_verified !== true) return false;
      if (filters.city && shelter.city !== filters.city) return false;
      return true;
    });

    // Add distance if user location is available
    if (userLocation) {
      result = result.map(shelter => ({
        ...shelter,
        distance: calculateDistance(userLocation[0], userLocation[1], shelter.lat, shelter.lng)
      }));
    }

    // Sort: shelters with address first, then by distance (if available) or name
    result.sort((a: ShelterWithDistance, b: ShelterWithDistance) => {
      const aHasAddress = hasExactLocation(a);
      const bHasAddress = hasExactLocation(b);
      
      // Shelters without address go to the end
      if (aHasAddress && !bHasAddress) return -1;
      if (!aHasAddress && bHasAddress) return 1;
      
      // If both have/don't have address, sort by distance or name
      if (userLocation && a.distance !== undefined && b.distance !== undefined) {
        return a.distance - b.distance;
      }
      
      return a.name.localeCompare(b.name, 'pl');
    });

    return result;
  }, [shelters, filters, userLocation, shelterIdsWithFundraiser, shelterIdsWithNamedAdoptions, shelterIdsWithUrgentSection]);

  // Dodatkowo: ograniczenie do widocznego obszaru mapy (jeśli włączone)
  const visibleShelters = useMemo((): ShelterWithDistance[] => {
    if (!filterByMapView || !mapBounds) return filteredShelters;
    const { north, south, east, west } = mapBounds;
    return filteredShelters.filter(
      (s) => s.lat <= north && s.lat >= south && s.lng <= east && s.lng >= west,
    );
  }, [filteredShelters, filterByMapView, mapBounds]);

  // Hook do batch-fetchu sekcji (potrzeby/zbiorki/adopcje) MUSI byc na top-level,
  // inaczej React rzuca "Rendered more hooks" gdy lista zmienia dlugosc.
  const visibleShelterIds = useMemo(() => visibleShelters.map((s) => s.id), [visibleShelters]);
  const { data: sectionsMap } = useShelterSections(visibleShelterIds);

  // Schroniska pasujące do wszystkich filtrów POZA searchQuery — używane jako
  // baza kandydatów dla sugestii "Czy chodziło Ci o…", aby kliknięcie sugestii
  // zawsze prowadziło do realnego wyniku (a nie kolejnego pustego ekranu).
  const sheltersForSuggestions = useMemo(() => {
    if (!shelters) return [];
    return shelters.filter((shelter) => {
      if (filters.dogWalking && shelter.dog_walking_available !== true) return false;
      if (filters.volunteering && shelter.volunteering_available !== true) return false;
      if (filters.urgentNeeds && !isUrgentNeedsActive(shelter) && !shelterIdsWithUrgentSection?.has(shelter.id)) return false;
      if (filters.hasFundraiser && !shelterIdsWithFundraiser?.has(shelter.id)) return false;
      if (filters.hasNamedAdoptions && !shelterIdsWithNamedAdoptions?.has(shelter.id)) return false;
      if (filters.dogsAvailable && shelter.dogs_available !== true) return false;
      if (filters.catsAvailable && shelter.cats_available !== true) return false;
      if (filters.organizationVerified && shelter.organization_verified !== true) return false;
      if (filters.city && shelter.city !== filters.city) return false;
      return true;
    });
  }, [shelters, filters.dogWalking, filters.volunteering, filters.urgentNeeds, filters.hasFundraiser, filters.hasNamedAdoptions, filters.dogsAvailable, filters.catsAvailable, filters.organizationVerified, filters.city, shelterIdsWithFundraiser, shelterIdsWithNamedAdoptions, shelterIdsWithUrgentSection]);

  // Track empty search results (debounced)
  // Prefetch chunka szybkiej aktualizacji w idle, żeby nawigacja była natychmiastowa.
  useEffect(() => {
    const idle = (cb: () => void) =>
      typeof window !== "undefined" && "requestIdleCallback" in window
        ? (window as any).requestIdleCallback(cb, { timeout: 2000 })
        : setTimeout(cb, 1500);
    idle(() => {
      prefetchQuickUpdate();
      prefetchShelterPage();
      prefetchPanel();
    });
  }, []);

  useEffect(() => {
    if (filters.searchQuery.length >= 3 && filteredShelters.length === 0) {
      const timeout = setTimeout(() => {
        trackEvent("Wyszukiwanie bez wyników", { query: filters.searchQuery });
      }, 1500);
      return () => clearTimeout(timeout);
    }
  }, [filters.searchQuery, filteredShelters.length]);

  // Track scroll position for fade effects
  useEffect(() => {
    const sidebar = sidebarRef.current;
    if (!sidebar) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = sidebar;
      setScrollPosition({
        atTop: scrollTop < 10,
        atBottom: scrollTop + clientHeight >= scrollHeight - 10
      });
    };

    sidebar.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check
    return () => sidebar.removeEventListener('scroll', handleScroll);
  }, [filteredShelters]);

  const handleShelterSelect = useCallback((shelter: Shelter, source: AnalyticsEventSource = "unknown") => {
    setSelectedShelter(shelter);
    setSelectedShelterSource(source);
    setShowDetail(true);
  }, []);

  const handleShelterSelectFromList = useCallback((shelter: Shelter) => handleShelterSelect(shelter, "list"), [handleShelterSelect]);
  const handleShelterSelectFromMap = useCallback((shelter: Shelter) => handleShelterSelect(shelter, "map"), [handleShelterSelect]);
  const handleShelterSelectFromSearch = useCallback((shelter: Shelter) => handleShelterSelect(shelter, "search"), [handleShelterSelect]);

  const handleCloseDetail = () => {
    setShowDetail(false);
    setSelectedShelter(null);
  };

  const handleClearSelection = () => {
    setSelectedShelter(null);
  };

  // Handle Escape key to close fullscreen map
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isMapFullscreen) {
        setIsMapFullscreen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isMapFullscreen]);

  const handleUserLocationChange = (location: [number, number] | null) => {
    setUserLocation(location);
  };

  const handleLocateUser = () => {
    if (!hasConsent) {
      toast.error("Musisz zaakceptować cookies, aby korzystać z geolokalizacji");
      return;
    }

    if (!navigator.geolocation) {
      toast.error("Twoja przeglądarka nie obsługuje geolokalizacji");
      return;
    }

    setIsLocating(true);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        
        if (latitude < 48.5 || latitude > 55.5 || longitude < 13.5 || longitude > 24.5) {
          toast.error("Twoja lokalizacja jest poza granicami Polski");
          setIsLocating(false);
          return;
        }

        setUserLocation([latitude, longitude]);
        setLocationDismissed(true);
        toast.success("Lista posortowana wg odległości od Ciebie!");
        setIsLocating(false);
      },
      (error) => {
        setIsLocating(false);
        const ua = navigator.userAgent;
        const isIOS =
          /iPad|iPhone|iPod/.test(ua) ||
          (ua.includes("Macintosh") && typeof navigator.maxTouchPoints === "number" && navigator.maxTouchPoints > 1);
        const isAndroid = /Android/i.test(ua);
        const platform: "ios" | "android" | "desktop" = isIOS ? "ios" : isAndroid ? "android" : "desktop";

        switch (error.code) {
          case error.PERMISSION_DENIED:
            // Show the nice inline popup instead of toast
            setLocationBlocked({ show: true, platform });
            break;
          case error.POSITION_UNAVAILABLE:
            toast.error("Nie udało się określić lokalizacji");
            break;
          default:
            toast.error("Błąd podczas pobierania lokalizacji");
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Header />

        {/* Skeleton Filters - dopasowany do glassmorphism stylu */}
        <div className="filters-surface px-3 py-3.5 sm:px-6 sm:py-4">
          <div className="flex gap-2 mb-2 sm:mb-2.5">
            <Skeleton className="h-9 flex-1 sm:max-w-xs rounded-full" />
            <Skeleton className="h-9 w-9 sm:w-40 rounded-full" />
          </div>
          <div className="flex flex-wrap gap-1.5 sm:gap-2">
            <Skeleton className="h-7 w-20 rounded-full" />
            <Skeleton className="h-7 w-28 rounded-full" />
            <Skeleton className="h-7 w-24 rounded-full" />
            <Skeleton className="h-7 w-24 rounded-full" />
            <Skeleton className="h-7 w-20 rounded-full" />
            <Skeleton className="h-7 w-16 rounded-full" />
            <Skeleton className="h-7 w-16 rounded-full" />
          </div>
        </div>

        {failureCount > 0 && (
          <div
            role="status"
            className="bg-amber-50 border-b border-amber-200 text-amber-900 text-sm px-4 py-2 text-center"
          >
            {LOADING_SLOW} ponawiamy próbę ({failureCount}/3).
          </div>
        )}

        <main className="flex-1 flex flex-col lg:flex-row">
          {/* Map placeholder during loading */}
          <div className="w-full lg:w-1/2 h-[35vh] min-h-[250px] lg:h-[calc(100vh-120px)] lg:sticky lg:top-0 lg:order-2 bg-muted/50 flex items-center justify-center">
            <span className="text-sm text-muted-foreground">Ładuję mapę…</span>
          </div>

          {/* Skeleton List */}
          <aside className="w-full lg:w-1/2 list-surface border-t lg:border-t-0 border-border/60 flex-1 lg:order-1 lg:h-[calc(100vh-120px)]">
            <div className="p-4">
              <Skeleton className="h-7 w-full max-w-xs rounded-full mb-3" />
              <ShelterListSkeleton count={4} />
            </div>
          </aside>
        </main>

        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <ErrorScreen
        title={isOnline ? "Nie udało się pobrać schronisk" : "Brak połączenia z internetem"}
        description={
          isOnline
            ? "Sprawdź połączenie z internetem i spróbuj ponownie. Jeśli problem się powtarza, daj nam znać."
            : "Wygląda na to, że jesteś offline. Połącz się z siecią i spróbuj ponownie."
        }
        details={error instanceof Error ? error.message : String(error)}
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <div className="bg-background flex flex-col animate-fade-in">
      <Helmet>
        {/* Title, description i og:* dziedziczone z index.html (identyczne dla "/").
            Tu trzymamy tylko canonical, którego nie ma w index.html. */}
        <link rel="canonical" href="https://gdzieschronisko.pl/" />
      </Helmet>
      <Header />
      {!isOnline && (
        <OfflineBanner onRetry={() => refetch()} isRetrying={isFetching} />
      )}

      
      {/* Hero Section */}
      <div ref={heroRef}>
        <HeroSection shelters={shelters || []} onScrollToMap={handleScrollToMap} onShelterSelect={handleShelterSelectFromSearch} />
      </div>
      
      {/* Filters Bar */}
      <div ref={(node) => { (mapRef as React.MutableRefObject<HTMLDivElement | null>).current = node; (filtersRef as React.MutableRefObject<HTMLDivElement | null>).current = node; }} className="relative z-[1000]">
        <ShelterFiltersComponent
        filters={filters}
        onFiltersChange={setFilters}
        cities={cities}
        resultsCount={filteredShelters.length}
        totalCount={shelters?.length || 0}
        shelters={shelters || []}
        shelterIdsWithFundraiser={shelterIdsWithFundraiser}
        shelterIdsWithNamedAdoptions={shelterIdsWithNamedAdoptions}
        shelterIdsWithUrgentSection={shelterIdsWithUrgentSection}
        />
      </div>

      {/* Spacer between tinted filters and content */}
      <div className="h-1.5 sm:h-2 bg-background" />

      <main className="flex flex-col lg:flex-row bg-background">
        {/* Map - always on top on mobile, right side on desktop */}
        <div ref={mapSectionRef} className="relative w-full lg:w-1/2 h-[35vh] min-h-[250px] lg:h-[calc(100vh-220px)] lg:sticky lg:top-4 lg:order-2 shadow-[inset_0_1px_0_hsl(var(--border))] lg:shadow-[inset_1px_0_0_hsl(var(--border))]">
          <ShelterMap
            shelters={filteredShelters}
            selectedShelterId={selectedShelter?.id ?? null}
            onShelterSelect={handleShelterSelectFromMap}
            onClearSelection={handleClearSelection}
            userLocation={userLocation}
            onUserLocationChange={handleUserLocationChange}
            isFullscreen={isMapFullscreen}
            onToggleFullscreen={() => setIsMapFullscreen(true)}
            focusCity={filters.city}
            urgentShelterIds={shelterIdsWithUrgentSection}
            onBoundsChange={setMapBounds}
          />

          {/* Floating "Szukaj w mojej okolicy" button - hidden when location is set or blocked dialog is open */}
          {!userLocation && !locationBlocked.show && (
            <button
              onClick={handleLocateUser}
              disabled={isLocating || !hasConsent}
              className="absolute bottom-7 left-3 sm:bottom-3 z-[400] inline-flex items-center gap-2 bg-background shadow-lg hover:shadow-xl border border-border/60 rounded-full pl-3 pr-4 py-2 sm:py-1.5 text-xs sm:text-[11px] font-semibold text-primary hover:bg-background/95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Szukaj w mojej okolicy"
            >
              <Locate className="w-3.5 h-3.5 sm:w-3 sm:h-3" />
              {isLocating ? "Szukam..." : "Szukaj w mojej okolicy"}
            </button>
          )}
        </div>

        {/* Sidebar - List (below map on mobile, left side on desktop) */}
        <aside ref={listRef} className="relative w-full lg:w-1/2 border-t lg:border-t-0 border-border/60 flex-1 lg:order-1 lg:h-[calc(100vh-220px)] lg:overflow-hidden lg:flex lg:flex-row">
          {/* Left gutter - allows page scroll on desktop, matches list surface */}
          <div className="hidden lg:flex w-2.5 min-w-[10px] flex-shrink-0 cursor-default list-surface" />

          {/* Scrollable content area */}
          <div className="relative flex-1 h-full list-surface min-w-0">
            {/* Scroll progress bar */}
            <ScrollProgressBar containerRef={sidebarRef as React.RefObject<HTMLElement>} />

            {/* Top fade gradient */}
            <div
              className={`hidden lg:block absolute top-0 left-0 right-0 h-8 bg-gradient-to-b from-background/80 to-transparent z-10 pointer-events-none transition-opacity duration-300 ${scrollPosition.atTop ? 'opacity-0' : 'opacity-100'}`}
            />

            <div ref={sidebarRef} className="h-full lg:overflow-y-auto px-3 sm:px-4 pt-1 sm:pt-1.5 pb-4 custom-scrollbar">
              {/* Location blocked info popup */}
              {locationBlocked.show && (
                <div className="mb-4">
                  <LocationBlockedInfo
                    platform={locationBlocked.platform}
                    onClose={() => {
                      setLocationBlocked({ show: false, platform: "desktop" });
                      setLocationDismissed(true);
                    }}
                  />
                </div>
              )}

              {/* Location prompt removed - use map's "szukaj w mojej okolicy" button instead */}

              {/* Community banner przeniesiony do pływającego CommunityPopup w lewym dolnym rogu */}


              {/* Results count + filtr "tylko widoczne na mapie" - glassy pill bar */}
              <div className="flex items-center justify-between gap-3 mb-2 flex-wrap text-[10px] sm:text-[11px] text-muted-foreground glass-pill px-3 py-1">
                <span className="font-medium">
                  Znaleziono <span className="text-foreground font-bold tabular-nums">{visibleShelters.length}</span> {(() => {
                    const n = visibleShelters.length;
                    const lastTwo = n % 100;
                    const last = n % 10;
                    if (n === 1) return 'schronisko';
                    if (last >= 2 && last <= 4 && (lastTwo < 12 || lastTwo > 14)) return 'schroniska';
                    return 'schronisk';
                  })()}
                  {userLocation && !filterByMapView && ' (wg odległości)'}
                  {filterByMapView && ' na mapie'}
                </span>
                <label htmlFor="map-view-toggle" className="inline-flex items-center gap-2 cursor-pointer select-none">
                  <span>Tylko widoczne na mapie</span>
                  <Switch
                    id="map-view-toggle"
                    checked={filterByMapView}
                    onCheckedChange={setFilterByMapView}
                    className="scale-75 origin-right"
                  />
                </label>
              </div>

              <ShelterList
                shelters={visibleShelters}
                selectedShelterId={selectedShelter?.id ?? null}
                onShelterSelect={handleShelterSelectFromList}
                containerRef={sidebarRef}
                onClearFilters={handleClearFilters}
                hasActiveFilters={hasActiveFilters || filterByMapView}
                searchQuery={filters.searchQuery}
                filterCity={filters.city}
                allShelters={sheltersForSuggestions as any}
                onSuggestionClick={(q) => setFilters({ ...filters, searchQuery: q })}
                sectionsMap={sectionsMap}
                suggestionsEnabled={
                  !isLoading &&
                  !error &&
                  filteredShelters.length === 0 &&
                  sheltersForSuggestions.length > 0 &&
                  filters.searchQuery.trim().length >= 3
                }
              />
            </div>
            
            {/* Bottom fade gradient */}
            <div
              className={`hidden lg:block absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-background/80 to-transparent z-10 pointer-events-none transition-opacity duration-300 ${scrollPosition.atBottom ? 'opacity-0' : 'opacity-100'}`}
            />
          </div>
        </aside>
      </main>

      {/* Fullscreen Map Modal */}
      {isMapFullscreen && (
        <div className="fixed inset-0 z-[1000] bg-background">
          <ShelterMap
            shelters={filteredShelters}
            selectedShelterId={selectedShelter?.id ?? null}
            onShelterSelect={handleShelterSelectFromMap}
            onClearSelection={handleClearSelection}
            userLocation={userLocation}
            onUserLocationChange={handleUserLocationChange}
            isFullscreen={isMapFullscreen}
            onToggleFullscreen={() => setIsMapFullscreen(false)}
            urgentShelterIds={shelterIdsWithUrgentSection}
          />
        </div>
      )}

      {/* Detail Modal */}
      {showDetail && selectedShelter && (
        <Suspense fallback={null}>
          <ShelterDetail
            shelter={selectedShelter}
            onClose={handleCloseDetail}
            source={selectedShelterSource}
          />
        </Suspense>
      )}

      <div ref={footerRef}>
        <Footer />
      </div>

      <CommunityPopup />
    </div>
  );
};

export default Index;
